import { useSelector } from "react-redux";
import BannerOne from "../../images/banner1.png";
import BannerTwo from "../../images/banner2.png";
import BannerThree from "../../images/banner3.png";
import BannerFour from "../../images/banner4.png";
import BannerFive from "../../images/banner5.png";
import BannerSix from "../../images/banner6.png";
import zedLogo from '../../images/logo-upload@3x.png';

// Short blurbs for the template gallery
export const templateDescriptions = {
    Zedify: 'The signature Zed look — fresh teal and clean lines.',
    Essence: 'Warm and energetic with bold orange accents.',
    Modern: 'Monochrome minimalism with a vivid orange accent.',
    Fashion: 'Editorial black and gold for apparel and style brands.',
    Sports: 'High-energy red built for jerseys and sportswear.',
    Tech: 'Crisp blue and clean type for gadgets and electronics.',
    Fresh: 'Natural greens for grocery, food and organic stores.',
    Beauty: 'Soft rose tones for cosmetics and self-care brands.',
    Playful: 'Bright, rounded and friendly — great for kids and toys.',
    Minimal: 'Pure black and white with sharp, square edges.',
    Lavender: 'Calm purple palette for gifts, decor and lifestyle.',
};

// Design-only presets: they restyle the store (colors, fonts, buttons, layout)
// without overwriting the merchant's logo, banners or content sections.
const designTemplates = {
    'Modern': {
        template: 'Modern',
        colors: { primary: '#1A1A1A', secondary: '#F5F5F5', tertiary: '#9E9E9E', quaternary: '#FF6B35', headings: '#1A1A1A', body: '#616161' },
        typography: { headings: 'Montserrat', body: 'Montserrat', baseSize: 'medium', headingSize: 'medium', buttonSize: 'medium' },
        buttons: { primary: '#1A1A1A', hover: '#FF6B35', borderRadius: 4 },
        layout: { productGridColumns: 4 },
        preOrderBadgeType: 'normal',
    },
    'Fashion': {
        template: 'Fashion',
        colors: { primary: '#000000', secondary: '#F8F5F0', tertiary: '#8A8A8A', quaternary: '#C9B037', headings: '#000000', body: '#4A4A4A' },
        typography: { headings: 'Playfair Display', body: 'Lato', baseSize: 'medium', headingSize: 'large', buttonSize: 'medium' },
        buttons: { primary: '#000000', hover: '#C9B037', borderRadius: 0 },
        layout: { productGridColumns: 4 },
        preOrderBadgeType: 'normal',
    },
    'Sports': {
        template: 'Sports',
        colors: { primary: '#DC2626', secondary: '#FEE2E2', tertiary: '#9E9E9E', quaternary: '#1A1A1A', headings: '#1A1A1A', body: '#4A4A4A' },
        typography: { headings: 'Oswald', body: 'Open Sans', baseSize: 'medium', headingSize: 'large', buttonSize: 'medium' },
        buttons: { primary: '#DC2626', hover: '#B91C1C', borderRadius: 8 },
        layout: { productGridColumns: 4 },
        preOrderBadgeType: 'transformed',
    },
    'Tech': {
        template: 'Tech',
        colors: { primary: '#0077BE', secondary: '#E3F2FD', tertiary: '#90A4AE', quaternary: '#00A8CC', headings: '#102A43', body: '#486581' },
        typography: { headings: 'Roboto', body: 'Roboto', baseSize: 'medium', headingSize: 'medium', buttonSize: 'medium' },
        buttons: { primary: '#0077BE', hover: '#005A8E', borderRadius: 6 },
        layout: { productGridColumns: 4 },
        preOrderBadgeType: 'normal',
    },
    'Fresh': {
        template: 'Fresh',
        colors: { primary: '#228B22', secondary: '#E8F5E9', tertiary: '#8D9E8D', quaternary: '#F57C00', headings: '#1B3A1B', body: '#4E5D4E' },
        typography: { headings: 'Nunito', body: 'Nunito', baseSize: 'medium', headingSize: 'medium', buttonSize: 'medium' },
        buttons: { primary: '#228B22', hover: '#1B6E1B', borderRadius: 12 },
        layout: { productGridColumns: 5 },
        preOrderBadgeType: 'normal',
    },
    'Beauty': {
        template: 'Beauty',
        colors: { primary: '#E91E63', secondary: '#FCE4EC', tertiary: '#B0A3A8', quaternary: '#9B59B6', headings: '#3D2530', body: '#6E5A63' },
        typography: { headings: 'Raleway', body: 'Nunito Sans', baseSize: 'medium', headingSize: 'medium', buttonSize: 'medium' },
        buttons: { primary: '#E91E63', hover: '#C2185B', borderRadius: 16 },
        layout: { productGridColumns: 4 },
        preOrderBadgeType: 'circular',
    },
    'Playful': {
        template: 'Playful',
        colors: { primary: '#FF9800', secondary: '#FFF3E0', tertiary: '#A1887F', quaternary: '#29B6F6', headings: '#4E342E', body: '#6D4C41' },
        typography: { headings: 'Comfortaa', body: 'Nunito', baseSize: 'large', headingSize: 'large', buttonSize: 'large' },
        buttons: { primary: '#FF9800', hover: '#F57C00', borderRadius: 20 },
        layout: { productGridColumns: 3 },
        preOrderBadgeType: 'circular',
    },
    'Minimal': {
        template: 'Minimal',
        colors: { primary: '#000000', secondary: '#F5F5F5', tertiary: '#9E9E9E', quaternary: '#666666', headings: '#000000', body: '#555555' },
        typography: { headings: 'Lato', body: 'Lato', baseSize: 'medium', headingSize: 'small', buttonSize: 'small' },
        buttons: { primary: '#000000', hover: '#333333', borderRadius: 0 },
        layout: { productGridColumns: 4 },
        preOrderBadgeType: 'normal',
    },
    'Lavender': {
        template: 'Lavender',
        colors: { primary: '#6A4C93', secondary: '#F3E8FF', tertiary: '#9E93AC', quaternary: '#F7971E', headings: '#2E2140', body: '#5D5470' },
        typography: { headings: 'Poppins', body: 'Poppins', baseSize: 'medium', headingSize: 'medium', buttonSize: 'medium' },
        buttons: { primary: '#6A4C93', hover: '#563D78', borderRadius: 10 },
        layout: { productGridColumns: 4 },
        preOrderBadgeType: 'normal',
    },
};

const useGetTemplates = () => {
    let { businessName } = useSelector((store) => store.configs);
    const { logo } = useSelector((state) => state.template);
    if(!businessName){
        businessName ='Zed';
    }
    if(logo?.logoText){
        businessName = logo?.logoText;
    }
    const templates = {
        'Zedify': {
            template: 'Zedify',
            logo: {
                logoType: 'text',
                logoPath: zedLogo,
                faviconPath: zedLogo,
                faviconGenerated: true,
                faviconFontFamily: 'Poppins',
                logoText: businessName?.charAt(0),
                slogan: `${businessName} eCommerce`,
            },
            colors: {
                primary:'#02B3AD',
                secondary:'#E0F4F3',
                tertiary: '#898989',
                quaternary: '#707070',
                headings: '#343434',
                body: '#707070',
            },
            typography: {
                headings: 'Poppins',
                body: 'Poppins',
                baseSize: 'medium',
                headingSize: 'medium',
                buttonSize: 'medium',
            },
            buttons: {
                primary: '#02B3AD',
                hover: '#02B3AD',
                borderRadius: 5,
            },
            layout: {
                productGridColumns: 4,
            },
            banners: {
                numberOfBanners: 6,
                path: [
                    { id: 0, url: BannerOne },
                    { id: 1, url: BannerTwo },
                    { id: 2, url: BannerThree },
                    { id: 3, url: BannerFour },
                    { id: 4, url: BannerFive },
                    { id: 5, url: BannerSix }
                ],
            },
            preOrderBadgeType: 'normal',
            disclaimer: {
                termsAndConditions: '',
                privaryPolicy: '',
                shippingPolicy: '',
                returnsAndExchange: '',
                faqs:''
            },
            topBar:{
                showTopBar: true,
                leftSectionIcon:'AddShoppingCart',
                leftSectionText:'Free shipping for Order of KES 10,000.00',
                middleSectionIcon:'Notifications',
                middleSectionText:'We have restocked! Get your order today.',
            },
            socialMedia:{
                facebook:'https://www.facebook.com/ZedPayments',
                instagram:'https://www.instagram.com/ZedPayments',
                x:'https://www.x.com/ZedPayments',
                linkedIn:'https://www.linkedin.com/ZedPayments',
                tikTok:'https://www.tiktok.com/ZedPayments',
            }
        },
        'Essence': {
            template: 'Essence',
            logo: {
                logoType: 'text',
                logoPath: zedLogo,
                faviconPath: zedLogo,
                faviconGenerated: true,
                faviconFontFamily: 'Roboto',
                logoText: businessName?.charAt(0),
                slogan: `${businessName} eCommerce`,
            },
            colors: {
                primary: '#FF5722', 
                secondary: '#FFCCBC', 
                tertiary: '#9E9E9E',
                quaternary: '#424242',
                headings: '#212121', 
                body: '#757575', 
            },
            typography: {
                headings: 'Roboto',
                body: 'Roboto',
                baseSize: 'medium',
                headingSize: 'medium',
                buttonSize: 'medium',
            },
            buttons: {
                primary: '#FF5722',
                hover: '#E64A19',
                borderRadius: 8,
            },
            layout: {
                productGridColumns: 4,
            },
            banners: {
                numberOfBanners: 1,
                path: [
                    { id: 0, url: BannerOne },
                ],
            },
            preOrderBadgeType: 'circular',
            disclaimer: {
                termsAndConditions: '',
                privaryPolicy: '',
                shippingPolicy: '',
                returnsAndExchange: '',
                faqs:''
            },
            topBar:{
                showTopBar: false,
                leftSectionIcon:'',
                leftSectionText:'',
                middleSection:'',
                middleSectionText:''
            },
            socialMedia:{
                facebook:'',
                instagram:'',
                x:'',
                linkedIn:'',
                tikTok:'',
            }
        },
        ...designTemplates,
    };

    return templates;
};

export default useGetTemplates;
